import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    EnvelopeSimple,
    Phone,
    User,
    House,
    Eye,
    Trash,
    CheckCircle,
    Clock,
    ArrowsClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
    { value: 'all', label: 'Alle' },
    { value: 'new', label: 'Neu' },
    { value: 'in_progress', label: 'In Bearbeitung' },
    { value: 'completed', label: 'Erledigt' },
];

const STATUS_CONFIG = {
    new: { label: 'Neu', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    in_progress: { label: 'In Bearbeitung', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    completed: { label: 'Erledigt', color: 'bg-green-50 text-green-700 border-green-200' },
};

export default function InquiriesPage() {
    const [inquiries, setInquiries] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    const fetchInquiries = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== 'all') params.append('status', statusFilter);
            params.append('limit', '50');
            const { data } = await axios.get(`${API}/api/inquiries?${params}`, { withCredentials: true });
            setInquiries(data.inquiries);
            setTotal(data.total);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

    const updateStatus = async (id, newStatus) => {
        try {
            await axios.put(`${API}/api/inquiries/${id}/status`, { status: newStatus }, { withCredentials: true });
            toast.success('Status aktualisiert');
            fetchInquiries();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    const deleteInquiry = async (id) => {
        if (!window.confirm('Anfrage wirklich löschen?')) return;
        try {
            await axios.delete(`${API}/api/inquiries/${id}`, { withCredentials: true });
            toast.success('Anfrage gelöscht');
            fetchInquiries();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6" data-testid="inquiries-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                        Kontaktanfragen
                    </h1>
                    <p className="text-muted-foreground mt-1">{total} Anfrage(n) insgesamt</p>
                </div>
                <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[180px] rounded-sm" data-testid="status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={fetchInquiries} className="rounded-sm" data-testid="refresh-btn">
                        <ArrowsClockwise size={16} />
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
            ) : inquiries.length === 0 ? (
                <Card className="border">
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                        <EnvelopeSimple size={48} className="text-muted-foreground/30 mb-4" />
                        <p className="text-lg font-medium">Keine Anfragen</p>
                        <p className="text-sm text-muted-foreground mt-1">Kontaktanfragen von Interessenten erscheinen hier</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {inquiries.map((inq) => {
                        const statusCfg = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;
                        const isExpanded = expandedId === inq.id;
                        return (
                            <Card
                                key={inq.id}
                                className={`border cursor-pointer transition-all hover:shadow-sm ${isExpanded ? 'ring-1 ring-primary/20' : ''}`}
                                onClick={() => setExpandedId(isExpanded ? null : inq.id)}
                                data-testid={`inquiry-${inq.id}`}
                            >
                                <CardContent className="py-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Badge variant="outline" className={`rounded-sm text-xs ${statusCfg.color}`}>
                                                    {statusCfg.label}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">{formatDate(inq.created_at)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <User size={14} className="text-muted-foreground shrink-0" />
                                                <span className="font-medium text-sm">{inq.name}</span>
                                                <span className="text-xs text-muted-foreground">({inq.email})</span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <House size={14} className="text-muted-foreground shrink-0" />
                                                {inq.property_object_nr && (
                                                    <Badge variant="outline" className="rounded-sm text-xs font-mono shrink-0" data-testid={`inquiry-objnr-${inq.id}`}>
                                                        Obj. {inq.property_object_nr}
                                                    </Badge>
                                                )}
                                                <span className="text-sm text-muted-foreground truncate">{inq.property_title}</span>
                                                {inq.broker_name && (
                                                    <Badge variant="outline" className="rounded-sm text-xs ml-1">{inq.broker_name}</Badge>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                                            {inq.status === 'new' && (
                                                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" onClick={() => updateStatus(inq.id, 'in_progress')} title="In Bearbeitung">
                                                    <Clock size={14} />
                                                </Button>
                                            )}
                                            {inq.status !== 'completed' && (
                                                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-green-600" onClick={() => updateStatus(inq.id, 'completed')} title="Erledigt">
                                                    <CheckCircle size={14} />
                                                </Button>
                                            )}
                                            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-destructive" onClick={() => deleteInquiry(inq.id)} title="Löschen">
                                                <Trash size={14} />
                                            </Button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="mt-4 pt-4 border-t space-y-3">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <EnvelopeSimple size={14} className="text-muted-foreground" />
                                                    <a href={`mailto:${inq.email}`} className="text-primary hover:underline">{inq.email}</a>
                                                </div>
                                                {inq.phone && (
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Phone size={14} className="text-muted-foreground" />
                                                        <a href={`tel:${inq.phone}`} className="text-primary hover:underline">{inq.phone}</a>
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground mb-1">Nachricht</p>
                                                <p className="text-sm bg-muted/50 rounded-sm p-3 whitespace-pre-wrap">{inq.message}</p>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
